// Pop's voice: a per-glyph mumble, ported from the Godot BartenderBeats scheduler.
//
// Nine tiny dry samples (audio/sfx/story/pop_voice_*.wav from Godot): six voiced blips, a
// ~100 Hz male glottal source through two formants per vowel colour, and three unvoiced
// consonant ticks. The line is walked glyph by glyph: vowels fire a voiced blip, other letters
// a tick, punctuation nothing (that IS the comma). Minimum gaps hold the rate near 13-15
// events a second whatever the length, so it reads as speech and never as a buzz. A deadpan
// contour falls through the line; a question turns the last fifth back up.
//
// The whole line is scheduled up front on the audio clock (no per-frame queue), across the
// same window as an on-screen typewriter: clamp(len * 0.016, 0.25, 1.3) s, so the mouth stops
// when the text stops. say() returns that window so a caption can reveal in step.

export type VoiceTag = 'neutral' | 'wipe' | 'pour' | 'slide' | 'lean' | 'shrug' | 'point' | 'still';

/** tag -> [pitch multiplier, dB offset, gap multiplier, end-of-line lift] (Godot VOICE_TAG_COLOR) */
const TAG: Record<VoiceTag, [number, number, number, number]> = {
  neutral: [1.0, 0, 1.0, 0],
  wipe: [1.0, -1, 1.05, 0],
  pour: [0.99, 0, 1.05, 0],
  slide: [1.01, 0, 0.98, 0.03],
  lean: [0.97, 1, 1.05, 0],
  shrug: [1.02, 0, 0.96, 0.06],
  point: [1.03, 0.5, 0.94, 0],
  still: [0.93, -2, 1.35, -0.04],
};
const GAP_VOICED = 0.055, GAP_TICK = 0.085;
const DB_VOICED = -5, DB_TICK = 0;

export class PopVoice {
  private ends: AudioBufferSourceNode[] = [];
  private out: GainNode;

  constructor(private ctx: AudioContext, bus: AudioNode, private buf: (stem: string) => AudioBuffer | null) {
    this.out = ctx.createGain();
    this.out.gain.value = 1.1;
    this.out.connect(bus);
  }

  /** speak `text`; cuts any line still talking. Returns the line's length in seconds. */
  say(text: string, tag: VoiceTag = 'neutral'): number {
    const ctx = this.ctx;
    for (const s of this.ends) { try { s.stop(); } catch { /* done */ } }
    this.ends = [];
    const n = text.length;
    const dur = Math.max(0.25, Math.min(1.3, n * 0.016));
    if (ctx.state !== 'running' || !this.buf('pop_a')) return dur;
    const [pMul, dbOff, gapMul, lift] = TAG[tag] ?? TAG.neutral;
    const per = dur / Math.max(n, 1), gapV = GAP_VOICED * gapMul, gapT = GAP_TICK * gapMul;
    const rising = text.trimEnd().endsWith('?');
    const t0 = ctx.currentTime + 0.02;
    let last = -1, tick = 0;
    for (let i = 0; i < n; i++) {
      const c = text[i].toLowerCase();
      const at = i * per;
      const voiced = 'aeiou'.includes(c);
      const consonant = !voiced && c >= 'a' && c <= 'z';
      if (!voiced && !consonant) continue;
      if (at - last < (voiced ? gapV : gapT)) continue;
      last = at;
      const f = i / Math.max(n, 1);
      let contour = 0.045 - 0.11 * f + lift * f;
      if (rising && f > 0.8) contour += 0.16 * ((f - 0.8) / 0.2);
      // "m" is the closed-mouth hum: it stands in for the schwa so a long word doesn't sing
      const stem = voiced ? (i % 5 !== 4 ? 'pop_' + c : 'pop_m') : 'pop_t' + (tick++ % 3);
      const b = this.buf(stem);
      if (!b) continue;
      const src = ctx.createBufferSource();
      src.buffer = b;
      src.playbackRate.value = Math.max(0.4, pMul * (1 + contour) * (0.97 + Math.random() * 0.06));
      const g = ctx.createGain();
      g.gain.value = Math.pow(10, ((voiced ? DB_VOICED : DB_TICK) + dbOff) / 20);
      src.connect(g); g.connect(this.out);
      src.start(t0 + at);
      src.onended = () => g.disconnect();
      this.ends.push(src);
    }
    return dur;
  }
}
